import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import {
  getAllTrees,
  fetchTreeSpecies,
  getPossibleChildTrees,
  getPossibleParentTrees,
} from "@modules/Trees/services/treeCrud";
import { getAllSensorsBatched } from "@modules/Sensors/services/sensorCrud";
import { getCustomerBasicInfo } from "@modules/Customer/services/customerCrud";
import { getAllProjects } from "@modules/Projects/services/projectCrud";
import {
  getQueryConfig,
  REQUEST_PRIORITIES,
  createScheduledQueryFn,
} from "@helper/requestPriority";
import { useEffect } from "react";

// Robust authentication check hook with browser detection and additional safeguards
const useAuthenticationCheck = () => {
  const authState = useSelector((state: any) => state.auth);

  // Chrome-specific checks
  const isChrome =
    typeof window !== "undefined" &&
    /Chrome/.test(navigator.userAgent) &&
    !/Edg|OPR|Brave/.test(navigator.userAgent);

  // Additional safeguards for Chrome persistence issues
  const localStorageCheck = () => {
    try {
      if (typeof window === "undefined") return false;
      const persistedState = localStorage.getItem("persist:root");
      if (!persistedState) return false;

      const parsed = JSON.parse(persistedState);
      const auth = parsed.auth ? JSON.parse(parsed.auth) : null;

      // If localStorage auth doesn't match Redux auth, something's wrong
      return Boolean(auth?.authToken && auth.authToken !== "undefined");
    } catch (e) {
      console.warn("[Auth Check] LocalStorage check failed:", e);
      return false;
    }
  };

  const isAuthenticated = !!(
    authState?.authToken &&
    authState?.user &&
    authState.authToken !== "undefined" &&
    authState.authToken !== null &&
    // Additional Chrome-specific check
    (isChrome ? localStorageCheck() : true)
  );

  // Enhanced debug logging with localStorage info
  useEffect(() => {
    const persistedState =
      typeof window !== "undefined" ? localStorage.getItem("persist:root") : null;
    let persistedAuth = null;

    try {
      if (persistedState) {
        const parsed = JSON.parse(persistedState);
        persistedAuth = parsed.auth ? JSON.parse(parsed.auth) : null;
      }
    } catch (e) {
      persistedAuth = { error: "Failed to parse" };
    }

    console.debug("[Auth Check - Enhanced]", {
      hasToken: !!authState?.authToken,
      hasUser: !!authState?.user,
      tokenValue: authState?.authToken ? "present" : "missing",
      isAuthenticated,
      browser: isChrome ? "Chrome" : "Other",
      userAgent: navigator.userAgent.substring(0, 50),
      persistedAuthExists: !!persistedAuth,
      persistedTokenExists: !!persistedAuth?.authToken,
      reduxPersistMatch: authState?.authToken === persistedAuth?.authToken,
    });
  }, [authState, isAuthenticated, isChrome]);

  return isAuthenticated;
};

// Helper function to check if user has paid
const useUserPaidStatus = () => {
  const authState = useSelector((state: any) => state.auth);
  return authState?.user?.paid || false;
};

// Helper function to ensure boolean values for Chrome protection
const getChromeProtection = (isAuthenticated: boolean): boolean => {
  const isChrome =
    typeof window !== "undefined" &&
    /Chrome/.test(navigator.userAgent) &&
    !/Edg|OPR|Brave/.test(navigator.userAgent);

  if (!isChrome) return true;

  // Chrome-specific checks that must return boolean
  return Boolean(
    isAuthenticated && typeof window !== "undefined" && localStorage.getItem("persist:root")
  );
};

// Trees
export const useBackgroundTrees = (enabled = true) => {
  const isAuthenticated = useAuthenticationCheck();
  const isPaid = useUserPaidStatus();
  const chromeProtection = getChromeProtection(isAuthenticated);
  const priority = REQUEST_PRIORITIES.LOW;
  const qfn = createScheduledQueryFn(getAllTrees, priority, "getAllTrees");

  const {
    data: trees,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery("trees", qfn as any, {
    ...getQueryConfig(priority, { requireAuth: false, respectLoadLimits: false }),
    // Only load trees for paid users
    enabled: Boolean(enabled && isAuthenticated && chromeProtection && isPaid),
    staleTime: 10 * 60 * 1000,
    cacheTime: 20 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    useErrorBoundary: false,
    select: (data: any[]) =>
      Array.isArray(data)
        ? data.map((t) => ({ value: t.id, label: t.name, id: t.id, name: t.name, ...t }))
        : [],
  });

  useEffect(() => {
    // any side effects...
  }, [isLoading]);

  return { trees: trees || [], isLoading, isError, error, refetch };
};

// Sensors
export const useBackgroundSensors = (enabled = true) => {
  const isAuthenticated = useAuthenticationCheck();
  const chromeProtection = getChromeProtection(isAuthenticated);
  const priority = REQUEST_PRIORITIES.LOW;
  const qfn = createScheduledQueryFn(getAllSensorsBatched, priority, "getAllSensorsBatched");

  const {
    data: sensors,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery("sensors", qfn as any, {
    ...getQueryConfig(priority, { requireAuth: false, respectLoadLimits: false }),
    enabled: Boolean(enabled && isAuthenticated && chromeProtection),
    staleTime: 15 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    useErrorBoundary: false,
    select: (data: any[]) =>
      Array.isArray(data)
        ? data.map((s) => ({
            value: s.id,
            label: s.hardware_serial || s.name || `Sensor ${s.id}`,
            id: s.id,
            name: s.name,
            hardware_serial: s.hardware_serial,
            ...s,
          }))
        : [],
  });

  return { sensors: sensors || [], isLoading, isError, error, refetch };
};

// Species
export const useBackgroundTreeSpecies = (enabled = true) => {
  const isAuthenticated = useAuthenticationCheck();
  const isPaid = useUserPaidStatus();
  const chromeProtection = getChromeProtection(isAuthenticated);
  const priority = REQUEST_PRIORITIES.LOW;
  const qfn = createScheduledQueryFn(fetchTreeSpecies, priority, "fetchTreeSpecies");

  const {
    data: species,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery("treeSpecies_v2", qfn as any, {
    ...getQueryConfig(priority, { requireAuth: false, respectLoadLimits: false }),
    // Only load tree species for paid users
    enabled: Boolean(enabled && isAuthenticated && chromeProtection && isPaid),
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    useErrorBoundary: false,
    select: (data: any) => {
      const list: any[] = Array.isArray(data) ? data : data?.results ?? [];
      return list.map((sp) => ({ value: sp.id, label: sp.name, id: sp.id, name: sp.name, ...sp }));
    },
  });

  return { treeSpecies: species || [], isLoading, isError, error, refetch };
};

// ================================================
// POSSIBLE CHILD TREES (no sensors) — same pattern
// ================================================
export const usePossibleChildTrees = (enabled = true) => {
  const isAuthenticated = useAuthenticationCheck();
  const isPaid = useUserPaidStatus();
  const chromeProtection = getChromeProtection(isAuthenticated);
  const priority = REQUEST_PRIORITIES.LOW;
  const qfn = createScheduledQueryFn(getPossibleChildTrees, priority, "getPossibleChildTrees");

  const {
    data: possibleChildTrees,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery("possibleChildTrees", qfn as any, {
    ...getQueryConfig(priority, { requireAuth: false, respectLoadLimits: false }),
    // Only load possible child trees for paid users
    enabled: Boolean(enabled && isAuthenticated && chromeProtection && isPaid),
    staleTime: 15 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    useErrorBoundary: false,
    select: (data: any[]) =>
      Array.isArray(data)
        ? data.map((t) => ({ value: t.id, label: t.name, id: t.id, name: t.name, ...t }))
        : [],
  });

  return { possibleChildTrees: possibleChildTrees || [], isLoading, isError, error, refetch };
};

// ================================================
// POSSIBLE PARENT TREES (with sensors) — same pattern
// ================================================
export const usePossibleParentTrees = (enabled = true) => {
  const isAuthenticated = useAuthenticationCheck();
  const isPaid = useUserPaidStatus();
  const chromeProtection = getChromeProtection(isAuthenticated);
  const priority = REQUEST_PRIORITIES.LOW;
  const qfn = createScheduledQueryFn(getPossibleParentTrees, priority, "getPossibleParentTrees");

  const {
    data: possibleParentTrees,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery("possibleParentTrees", qfn as any, {
    ...getQueryConfig(priority, { requireAuth: false, respectLoadLimits: false }),
    // Only load possible parent trees for paid users
    enabled: Boolean(enabled && isAuthenticated && chromeProtection && isPaid),
    staleTime: 15 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    useErrorBoundary: false,
    select: (data: any[]) =>
      Array.isArray(data)
        ? data.map((t) => ({ value: t.id, label: t.name, id: t.id, name: t.name, ...t }))
        : [],
  });

  return { possibleParentTrees: possibleParentTrees || [], isLoading, isError, error, refetch };
};

// ================================================
// CUSTOMER BASIC INFO — same pattern
// ================================================
export const useBackgroundCustomerBasicInfo = (enabled = true) => {
  const isAuthenticated = useAuthenticationCheck();
  const chromeProtection = getChromeProtection(isAuthenticated);
  const priority = REQUEST_PRIORITIES.LOW;
  const qfn = createScheduledQueryFn(getCustomerBasicInfo, priority, "getCustomerBasicInfo");

  const {
    data: customerBasicInfo,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery("customer-basic-info", qfn as any, {
    ...getQueryConfig(priority, { requireAuth: false, respectLoadLimits: false }),
    enabled: Boolean(enabled && isAuthenticated && chromeProtection),
    staleTime: 30 * 60 * 1000, // 30 minutes - customer info doesn't change often
    cacheTime: 60 * 60 * 1000, // 1 hour in cache
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    useErrorBoundary: false,
    select: (data: any[]) =>
      Array.isArray(data) && data.length > 0
        ? {
            ...data[0],
            id: data[0].id,
            name: data[0].name,
            image: data[0].image?.image || data[0].image,
            paid: data[0].paid,
            use_new_moisture_formula: data[0].use_new_moisture_formula,
            table_column_preferences: data[0].table_column_preferences,
          }
        : null,
  });

  return { customerBasicInfo, isLoading, isError, error, refetch };
};

// ================================================
// PROJECTS — background loading for navigation
// ================================================
export const useBackgroundProjects = (enabled = true) => {
  const isAuthenticated = useAuthenticationCheck();
  const isPaid = useUserPaidStatus();
  const chromeProtection = getChromeProtection(isAuthenticated);
  const priority = REQUEST_PRIORITIES.LOW;
  const qfn = createScheduledQueryFn(getAllProjects, priority, "getAllProjects");

  const {
    data: projects,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery("projects", qfn as any, {
    ...getQueryConfig(priority, { requireAuth: false, respectLoadLimits: false }),
    // Only load projects for paid users
    enabled: Boolean(enabled && isAuthenticated && chromeProtection && isPaid),
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 20 * 60 * 1000, // 20 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    useErrorBoundary: false,
    select: (data: any[]) =>
      Array.isArray(data)
        ? data.map((p) => ({
            value: p.id,
            label: p.name,
            id: p.id,
            name: p.name,
            city: p.city || p.location || null,
            tree_count: p.tree_count || 0,
            ...p,
          }))
        : [],
  });

  return { projects: projects || [], isLoading, isError, error, refetch };
};
